import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';

import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import zIndex from '@/styles/zIndex';

import { useFeedbackDetailContext } from '../../context/FeedbackDetailContext';
import { ValueWrapper } from '../ValueWrapper';
import { PropertyRowWrapper } from './PropertyRowWrapper';
import type { CommonFieldProps } from './types';

export const RatingScoreField = (
  props: CommonFieldProps & {
    feedback_alias: string;
    rating_score?: number;
    nps_score?: number;
    csat_score?: number;
    ces_score?: number;
  },
) => {
  const changeScore = useFeedbackListStore((state) => state.changeScore);
  const { updateDetail } = useFeedbackDetailContext();

  const onScoreChange = (value: number | null, field_name: string) => {
    changeScore(
      props.feedback_alias,
      value,
      field_name as 'rating_score' | 'nps_score' | 'ces_score' | 'csat_score',
    );
    updateDetail({
      [field_name]: value,
    });
  };

  if (!props.showFull && typeof props.rating_score === 'undefined') return null;

  return (
    <PropertyRowWrapper {...props}>
      <ScorePopover
        trigger={
          <ValueWrapper>
            <Typo type="label3" weight="m">
              {props.rating_score ?? '-'}
            </Typo>
          </ValueWrapper>
        }
        value={props.rating_score ?? 0}
        field_label="Rating Score"
        onDone={(value) => {
          onScoreChange(value, 'rating_score');
        }}
      />
    </PropertyRowWrapper>
  );
};

const ScorePopover = (props: {
  value: number;
  trigger: React.ReactNode;
  field_label: string;
  onDone: (value: number | null) => void;
}) => {
  const [value, setValue] = useState<number | null>(props.value);
  return (
    <Popover.Root>
      <Popover.Trigger>{props.trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="bg-gray-100 rounded-md p-2.5 border border-gray-200 shadow-md"
          style={{
            zIndex: zIndex.popup,
          }}
        >
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <Typo type="label3" weight="m">
                {props.field_label}
              </Typo>
              <Popover.Close>
                <Typo
                  type="label3"
                  weight="m"
                  className="text-gray-600 cursor-pointer hover:bg-gray-200 duration-200 transition-all py-1 px-2 rounded-sm"
                  onClick={() => {
                    props.onDone(null);
                  }}
                >
                  clear
                </Typo>
              </Popover.Close>
            </div>
            <Input
              type="number"
              sizeType="m"
              value={value ?? undefined}
              onChange={(e) => setValue(Number(e.target.value))}
              onEnter={() => props.onDone(value)}
            />
          </div>
          <Popover.Arrow className="fill-gray-100" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
